import KitFadeView from '@omni/kit/components/KitFadeView';
import KitIcon from '@omni/kit/components/KitIcon';
import KitText from '@omni/kit/components/KitText';
import BorderRadius from '@omni/kit/theming/BorderRadius';
import Colors from '@omni/kit/theming/Colors';
import Spacing from '@omni/kit/theming/Spacing';
import formatDuration from '@omni/kit/utilities/formatDuration';
import { transparentHexColor } from '@omni/kit/utilities/utilities';
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { StyleSheet, View } from 'react-native';

import { usePlayerVideoCurrentTime } from '../../../../../PlayerVideo';
import { PlayerTimelineProps, PlayerTimelineRef } from '../../../../types';

function calculatePlayPosition(
  currentTime: number,
  totalDuration?: number
): number {
  return currentTime > 0 && totalDuration
    ? (currentTime / totalDuration) * 100
    : 0;
}

const TvPlayerTimeline = forwardRef<PlayerTimelineRef, PlayerTimelineProps>(
  (
    {
      isVisible,
      isShowingTooltipTime,
      isFastSeeking,
      fastSeekDirection = 'forward',
      fastSeekMultiplierText,
      bufferedTimeSeconds = 0,
      totalDurationSeconds = -1,
      currentTimebarStyle,
      progressBarStyle,
      playheadStyle,
      playerVideoRef,
    },
    playerTimelineRef
  ) => {
    const { currentTime: currentTimeInSeconds, updateTime } =
      usePlayerVideoCurrentTime({
        playerVideoRef,
      });

    const [fastSeekTime, setFastSeekTime] = useState(0);
    const progressBarRef = useRef<View>(null);

    const currentTime =
      useMemo(() => {
        if (isFastSeeking) {
          return fastSeekTime;
        } else {
          return currentTimeInSeconds;
        }
      }, [isFastSeeking, fastSeekTime, currentTimeInSeconds]) || 0;

    const currentTimeString = useMemo(
      () => formatDuration(currentTime) ?? '––:––',
      [currentTime]
    );

    const reminaingTimeString = useMemo(() => {
      return totalDurationSeconds
        ? formatDuration(totalDurationSeconds - currentTime)
        : '––:––';
    }, [currentTime, totalDurationSeconds]);

    const percentage = useMemo(() => {
      if (!totalDurationSeconds) return 0;

      return (currentTimeInSeconds / totalDurationSeconds) * 100;
    }, [currentTimeInSeconds, totalDurationSeconds]);

    const playPosition = useMemo(
      () => calculatePlayPosition(currentTime, totalDurationSeconds),
      [currentTime, totalDurationSeconds]
    );

    const bufferedTimePercentage = useMemo(() => {
      return totalDurationSeconds
        ? (bufferedTimeSeconds / totalDurationSeconds) * 100
        : 0;
    }, [bufferedTimeSeconds, totalDurationSeconds]);

    useImperativeHandle(playerTimelineRef, () => ({
      fastSeekUpdate(seconds) {
        setFastSeekTime(seconds);
      },
      updateTime,
    }));

    return (
      <KitFadeView show={isVisible} keepElementSize style={styles.container}>
        <View style={styles.barContainer}>
          {totalDurationSeconds > 0 ? (
            <>
              <View
                ref={progressBarRef}
                style={[styles.progressBar, progressBarStyle]}
              >
                {/* Buffered time bar */}
                <View
                  style={[
                    styles.bufferedTimeBar,
                    {
                      width: `${bufferedTimePercentage}%`,
                    },
                  ]}
                />
                <View
                  style={[
                    styles.currentTimeBar,
                    currentTimebarStyle,
                    { width: `${percentage}%` },
                  ]}
                />
              </View>
              <View
                style={[styles.playheadContainer, { left: `${playPosition}%` }]}
              >
                <View style={[styles.playhead, playheadStyle]} />

                {/** Logic used to show tooltip for TV */}
                <KitFadeView
                  show={isFastSeeking || isShowingTooltipTime}
                  style={styles.tooltipContainer}
                >
                  <KitText style={styles.tooltipTime}>
                    {currentTimeString}
                  </KitText>
                </KitFadeView>
                <KitFadeView show={isFastSeeking} style={styles.fastSeekMult}>
                  {fastSeekDirection === 'backward' ? (
                    <KitIcon
                      name='seek-backward'
                      size={FontSize}
                      color={Colors.N0}
                    />
                  ) : null}

                  <KitText white fontSize={FontSize} semiBold>
                    {fastSeekMultiplierText}
                  </KitText>

                  {fastSeekDirection === 'forward' ? (
                    <KitIcon
                      name='seek-forward'
                      size={FontSize}
                      color={Colors.N0}
                    />
                  ) : null}
                </KitFadeView>
              </View>
            </>
          ) : null}
        </View>

        <KitFadeView
          show={!isShowingTooltipTime ? !isFastSeeking : undefined}
          style={[styles.timestamps]}
        >
          <KitText white fontSize={FontSize} style={styles.text}>
            {currentTimeString}
          </KitText>
          <KitText white fontSize={FontSize} style={styles.text}>
            {reminaingTimeString}
          </KitText>
        </KitFadeView>
      </KitFadeView>
    );
  }
);

const BarHeight = 6;
const PlayheadSize = 18;
const TextOffsetFromBar = BarHeight + 12;
const FontSize = 18;
const PlayheadContainerWidth = 200;

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flex: 1,
    zIndex: 2,
  },
  barContainer: {
    borderRadius: BorderRadius.full,
    height: BarHeight,
    position: 'relative',
    width: '100%',
  },
  progressBar: {
    alignSelf: 'center',
    width: '100%',
    height: Spacing.xs,
    backgroundColor: '#ffffff66',
    borderRadius: Spacing.xxs,
    overflow: 'hidden',
  },
  bufferedTimeBar: {
    position: 'absolute',
    height: Spacing.xs,
    backgroundColor: transparentHexColor(Colors.N0, 50),
    borderRadius: Spacing.xxs,
    bottom: 0,
  },
  currentTimeBar: {
    position: 'absolute',
    alignSelf: 'flex-start',
    height: Spacing.xs,
    bottom: 0,
    backgroundColor: Colors.N0,
  },
  /**
   * Takes care of the positioning of playhead so all elements can live inside
   * and offset vertically from it, but align horizontally to its center.
   */
  playheadContainer: {
    position: 'absolute',
    alignItems: 'center',
    marginLeft: -(PlayheadContainerWidth / 2),
    height: BarHeight,
    width: PlayheadContainerWidth,
  },
  playhead: {
    backgroundColor: Colors.N0,
    borderRadius: BorderRadius.full,
    height: PlayheadSize,
    top: -BarHeight,
    width: PlayheadSize,
  },
  tooltipContainer: {
    position: 'absolute',
    backgroundColor: transparentHexColor(Colors.N1000, 80),
    borderRadius: BorderRadius.full,
    paddingHorizontal: Spacing.l,
    paddingVertical: Spacing.s,
    bottom: Spacing.l,
    pointerEvents: 'none',
  },
  tooltipTime: {
    color: Colors.N0,
    fontSize: FontSize,
  },
  fastSeekMult: {
    position: 'absolute',
    flexDirection: 'row',
    justifyContent: 'center',
    paddingHorizontal: Spacing.l,
    gap: 4,
    top: BarHeight + 22,
  },
  text: {
    includeFontPadding: false,
    padding: 2,
    textShadowColor: transparentHexColor(Colors.N1000, 30),
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 12,
  },
  timestamps: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    left: 0,
    marginTop: 0,
    position: 'absolute',
    right: 0,
    top: TextOffsetFromBar,
  },
});

export default TvPlayerTimeline;
